import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { CheckCircle2, AlertCircle } from "lucide-react";
import { markCompanyPaid, type CompanySubscriptionView, coerceTier } from "../../../api/General/Subscription";
import { addDays, toInputDate } from "./helpers";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanySubscriptionView | null;
  onSuccess: () => void;
}

export default function MarkPaidDialog({ open, onOpenChange, company, onSuccess }: Props) {
  const { t } = useTranslation();

  const defaultDate = useMemo(() => toInputDate(addDays(new Date(), 365)), []);
  const [paidUntil, setPaidUntil] = useState<string>(defaultDate);
  const [indefinite, setIndefinite] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPaidUntil(defaultDate);
      setIndefinite(false);
      setNotes("");
      setError(null);
      setSubmitting(false);
    }
  }, [open, defaultDate]);

  const submit = async () => {
    if (!company) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await markCompanyPaid(company.id, {
        paidUntil: indefinite ? null : new Date(paidUntil).toISOString(),
        notes: notes.trim() || null,
      });
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || t("Failed to update subscription"));
        setSubmitting(false);
        return;
      }
      onSuccess();
      onOpenChange(false);
    } catch (e) {
      const message =
        (e as { response?: { data?: { errors?: { message?: string }[] } } })?.response?.data
          ?.errors?.[0]?.message || t("Failed to update subscription");
      setError(message);
      setSubmitting(false);
    }
  };

  if (!company) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center">
              <CheckCircle2 size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Mark as Paid")}</DialogTitle>
              <DialogDescription className="mt-1">
                {company.name}
                <span className="mx-1.5 text-muted-foreground/50">·</span>
                <span className="text-xs uppercase tracking-wider">{t(coerceTier(company.tier))}</span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("Marking paid will set tier to Paid and clear any active trial.")}
          </p>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="mp-paid-until">
              {t("Paid Until Date")}
            </label>
            <Input
              id="mp-paid-until"
              type="date"
              value={paidUntil}
              disabled={indefinite}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPaidUntil(e.target.value)}
            />
            <label className="flex items-center gap-2 text-sm text-foreground select-none">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-input text-primary focus:ring-2 focus:ring-primary/20"
                checked={indefinite}
                onChange={(e) => setIndefinite(e.target.checked)}
              />
              {t("Indefinite (no expiry)")}
            </label>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="mp-notes">
              {t("Notes (optional)")}
            </label>
            <textarea
              id="mp-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("Add a note about this change")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} variant="success">
            {t("Mark as Paid")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
